import { Suspense } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { useNgoAreaUrl } from '@/lib/ngo-area';
import { Eyebrow, PillLink } from '../primitives';
import { NGO_APPLICATION_FORM_URL } from '../external-links';
import { Expand, ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';
import { formatBandFigure } from '../figure-format';
import { GlobeAnimation } from '@worldcoo/ui/illustrations/animated/globe-animation';

/** The global-stats query, so the headline and the directory never disagree; Suspense wraps it alone. */
function PartnerCount() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{formatBandFigure(language, stats.socialOrganizations)}</>;
}

/** A grid, not absolute positioning, so it collapses to the hand-off's mobile column; 601 = 249 + 24 + 328. */
export function Hero() {
	const { t } = useLingui();
	const ngoArea = useNgoAreaUrl();
	return (
		<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
			<section className="mx-auto grid max-w-[1440px] items-center gap-6 px-6 py-12 md:py-16 lg:grid-cols-[536fr_369fr_369fr] xl:px-[54px]">
				<div className="flex flex-col items-start gap-6">
					<Eyebrow>{t`Partner NGOs`}</Eyebrow>
					<div className="flex flex-col gap-2">
						<h1 className="text-[2.5rem] leading-[1.143] font-medium tracking-[-0.02em] md:text-[3.5rem]">
							{t`We were born with a very clear purpose`}
						</h1>
						<p className="max-w-[481px] text-base leading-[1.5]">
							{t`Helping social entities find the funding they need to carry out their projects all over the world.`}
						</p>
					</div>
					{/* The mobile frame drops both pills; the header's menu carries the CTA there. */}
					<div className="hidden flex-wrap gap-2 lg:flex">
						<PillLink variant="ink" href={NGO_APPLICATION_FORM_URL}>{t`Join Worldcoo`}</PillLink>
						<PillLink variant="outline" href={ngoArea('/login')}>{t`I already collaborate`}</PillLink>
					</div>
				</div>

				{/* `height` asks 2x of a 472x768 source, and imgproxy clamps rather than enlarging. */}
				{/* `-v2` busts the CDN, which serves a replaced asset's old bytes for a year: rename, don't replace. */}
				<Expand index={0} asChild>
					<div className="relative h-60 overflow-hidden rounded-3xl lg:h-[601px]">
						<Image
							src="/redesign/partner-ngos/hero-children-circle-v2.jpg"
							alt={t`Children in a circle, arms around each other's shoulders, smiling down at the camera`}
							height={1202}
							aspectRatio={369 / 601}
							objectFit="cover"
							priority
							className="absolute inset-0 size-full object-cover!"
						/>
						{/* Tone-matching against the cards beside it, not legibility. */}
						<div aria-hidden className="absolute inset-0 bg-black/10" />
					</div>
				</Expand>

				<div className="flex flex-col gap-6">
					<Expand index={1} asChild>
						<div className="bg-sage flex h-[195px] flex-col justify-between gap-4 rounded-3xl p-6 lg:h-[249px] lg:gap-8 lg:p-8">
							<Zap className="text-ink size-9 shrink-0 fill-current" strokeWidth={0} />
							<div className="flex flex-col">
								<p className="text-ink text-5xl leading-[1.3] font-semibold tabular-nums md:text-[3.5rem]">
									<Suspense fallback={<span className="opacity-40">—</span>}>
										<PartnerCount />
									</Suspense>
								</p>
								<p className="text-ink text-xl leading-[1.5] font-medium md:text-2xl">{t`Partner NGOs`}</p>
							</div>
						</div>
					</Expand>

					{/* Clipped, not stretched: the illustration is not on the card's aspect ratio. */}
					<Expand index={1.5} asChild>
						<div className="bg-apricot flex h-[195px] items-center justify-center overflow-hidden rounded-3xl lg:h-[328px]">
							<GlobeAnimation
								label={t`A globe marked with the countries where the NGOs work`}
								className="h-full w-auto max-w-full lg:h-auto lg:w-full"
							/>
						</div>
					</Expand>
				</div>
			</section>
		</ExpandGroup>
	);
}
